import { EngineEvaluation, parseEngineInfo } from '@chess-platform/shared';
import { levelToSkillLevel, levelToElo } from '@chess-platform/shared';
export type { EngineEvaluation };

export type EngineCallback = (evalu: EngineEvaluation) => void;

/**
 * EngineManager — singleton Stockfish worker for LIVE ANALYSIS ONLY.
 * AI moves are handled by aiEngine.ts (a separate worker).
 * This separation ensures the player's position is always analyzed
 * without being interrupted by the AI's move calculation.
 */
class EngineManager {
    private worker: Worker | null = null;
    private isReady: boolean = false;
    private currentEval: EngineEvaluation = this.getEmptyEval();
    private isWaitingForReady: boolean = false;
    private nextAnalysis: { fen: string; goParams: string; ply: number; level: number } | null = null;

    private onReadyCb: (() => void) | null = null;
    private onEvalUpdateCbs: Set<EngineCallback> = new Set();
    private onErrorCbs: Set<(err: string) => void> = new Set();

    private currentTurn: 'w' | 'b' = 'w';
    private currentPly: number = 0;

    constructor() {
        this.init();
    }

    public init() {
        if (typeof window === 'undefined') return;
        if (this.worker) return;

        try {
            this.worker = new Worker('/stockfish.js');
        } catch (err) {
            console.error('EngineManager: Failed to init worker.', err);
            this.notifyError('El motor no pudo cargarse.');
            return;
        }

        this.worker.onerror = (e) => {
            console.error('EngineManager worker error:', e);
            this.notifyError('Error interno en el motor.');
            this.isReady = false;
        };

        this.worker.onmessage = this.handleMessage.bind(this);

        this.worker.postMessage('uci');
        // Activamos NNUE para la Verdad Absoluta (Mathematical Mode)
        this.worker.postMessage('setoption name Use NNUE value true');
        this.worker.postMessage('setoption name MultiPV value 3');
        this.worker.postMessage('setoption name Hash value 128');
        this.worker.postMessage('isready');
    }

    private getEmptyEval(): EngineEvaluation {
        return { score: null, isMate: false, mateMoves: null, bestMove: null, multipv: [], depth: 0, nps: 0 };
    }

    private handleMessage(e: MessageEvent) {
        const msg = e.data as string;

        if (msg === 'readyok') {
            this.isWaitingForReady = false;
            this.isReady = true;

            if (this.onReadyCb) {
                const cb = this.onReadyCb;
                this.onReadyCb = null;
                cb();
                return;
            }

            if (this.nextAnalysis) {
                const { fen, goParams, ply, level } = this.nextAnalysis;
                this.nextAnalysis = null;
                this.startActualAnalysis(fen, goParams, ply, level);
            }
            return;
        }

        if (this.isWaitingForReady) return;

        if (msg.startsWith('info depth')) {
            this.currentEval = parseEngineInfo(msg, this.currentTurn, this.currentEval);
            this.notifyEvalUpdate();
        }

        if (msg.startsWith('bestmove')) {
            const parts = msg.split(' ');
            const movePart = parts[1];
            if (movePart && movePart !== '(none)') {
                this.currentEval = { ...this.currentEval, bestMove: movePart };
                this.notifyEvalUpdate();
            }
        }
    }

    private notifyEvalUpdate() {
        const payload: any = {
            ...this.currentEval,
            multipv: [...this.currentEval.multipv],
            analysisTurn: this.currentTurn,
            ply: this.currentPly
        };
        for (const cb of this.onEvalUpdateCbs) {
            try { cb(payload as EngineEvaluation); } catch (_) { }
        }
    }

    private notifyError(err: string) {
        for (const cb of this.onErrorCbs) cb(err);
    }

    public onReady(cb: () => void) {
        if (this.isReady) cb();
        else this.onReadyCb = cb;
    }

    public getIsReady() { return this.isReady; }

    public subscribe(cb: EngineCallback): () => void {
        this.onEvalUpdateCbs.add(cb);
        cb({ ...this.currentEval, multipv: [...this.currentEval.multipv] });
        return () => this.onEvalUpdateCbs.delete(cb);
    }

    public onError(cb: (err: string) => void): () => void {
        this.onErrorCbs.add(cb);
        return () => this.onErrorCbs.delete(cb);
    }

    public stop() {
        this.worker?.postMessage('stop');
    }

    public analyze(fen: string, goParams: string = 'depth 15', ply: number = 0, level: number = 20) {
        if (!this.worker || !this.isReady) return;

        this.currentEval = this.getEmptyEval();
        this.stop();
        this.isWaitingForReady = true;
        // Forzamos level 20 para ANÁLISIS, ignorando niveles de juego bajos
        this.nextAnalysis = { fen, goParams, ply, level: 20 };
        this.worker.postMessage('isready');
    }

    private startActualAnalysis(fen: string, goParams: string, ply: number, level: number) {
        if (!this.worker) return;
        this.currentTurn = (fen.split(' ')[1] as 'w' | 'b') || 'w';
        this.currentPly = ply;
        this.applyLevelOptions(level);
        this.worker.postMessage(`position fen ${fen}`);
        this.worker.postMessage(`go ${goParams}`);
    }

    private applyLevelOptions(level: number) {
        const skill = levelToSkillLevel(level);
        const elo = levelToElo(level);
        this.worker?.postMessage(`setoption name Skill Level value ${skill}`);
        if (level < 20) {
            this.worker?.postMessage(`setoption name UCI_LimitStrength value true`);
            this.worker?.postMessage(`setoption name UCI_Elo value ${elo}`);
        } else {
            this.worker?.postMessage(`setoption name UCI_LimitStrength value false`);
        }
    }

    public setMultiPv(lines: number) {
        this.worker?.postMessage(`setoption name MultiPV value ${lines}`);
    }

    public setSkillLevel(level: number) {
        const clamped = Math.max(0, Math.min(20, level));
        this.worker?.postMessage(`setoption name Skill Level value ${clamped}`);
    }

    /**
     * Compatibility wrapper — delegates to aiEngine so existing callers
     * (OpeningsTheory, OpeningTraining, etc.) don't need to change.
     * NOTE: For the Play page, use aiEngine directly via useGameLogic.
     */
    public async getBestMoveOnce(
        fen: string, level: number = 20, moveNumber: number = 0
    ): Promise<string | null> {
        // Redirigimos al Motor de Cálculo (M2) especializado
        const { aiEngine } = await import('./aiEngine');
        return aiEngine.getBestMove(fen, level, moveNumber);
    }

    /**
     * Compatibility wrapper for analysis module.
     */
    public async getMovesForSquare(fen: string, square: string, depth: number = 15): Promise<EngineEvaluation> {
        return new Promise((resolve) => {
            if (!this.worker || !this.isReady) { resolve(this.getEmptyEval()); return; }
            this.setMultiPv(20);
            this.analyze(fen, `depth ${depth}`);
            setTimeout(() => {
                this.stop();
                const filtered = { ...this.currentEval, multipv: this.currentEval.multipv.filter(m => m.move?.startsWith(square)) };
                resolve(filtered);
            }, 3000);
        });
    }

    public destroy() {
        this.worker?.terminate();
        this.worker = null;
        this.onEvalUpdateCbs.clear();
        this.onErrorCbs.clear();
        this.isReady = false;
    }
}

export const engineManager = new EngineManager();
